import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, ImagePlus, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useArchiveTaskStatus,
  useCreateTag,
  useCreateTaskStatus,
  useDeleteGithubInstallation,
  useDeleteTag,
  useDeleteWorkspaceImage,
  useGetGithubInstallUrl,
  useGithubInstallation,
  useMyWorkspaceMembership,
  useReorderTaskStatus,
  useTags,
  useUpdateTag,
  useUpdateTaskStatus,
  useUpdateWorkspace,
  useUploadWorkspaceImage,
  useWorkspace,
  useWorkspaceStatuses,
  type StatusCategory,
} from '@fluxter/shared';
import { FluxterMark } from '@/components/FluxterMark';
import { TagChip } from '@/components/TagChip';
import { ColorSwatchPicker } from '@/components/ColorSwatchPicker';
import { useConfirm } from '@/components/ConfirmDialog';
import { SkeletonList, SkeletonStack } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DEFAULT_TAG_COLOR } from '@/lib/colors';
import { cn } from '@/lib/utils';

interface TagAttrs {
  name?: string;
  color?: string;
}

interface TagItem {
  id: string;
  attributes?: TagAttrs;
}

interface StatusAttrs {
  name?: string;
  category?: StatusCategory;
  color?: string;
  position?: number;
  is_default?: boolean;
  archived_at?: string | null;
}

interface StatusItem {
  id: string;
  attributes?: StatusAttrs;
}

const CATEGORY_LABEL: Record<StatusCategory, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  complete: 'Complete',
};
const CATEGORY_ORDER: StatusCategory[] = ['todo', 'in_progress', 'complete'];

const DEFAULT_STATUS_COLOR = '#64748b';

export const WorkspaceSettingsPage = () => {
  const { workspaceSlug } = useParams({ from: '/w/$workspaceSlug/settings' });
  const tags = useTags(workspaceSlug);
  const createTag = useCreateTag(workspaceSlug);

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<string>(DEFAULT_TAG_COLOR);
  const [createError, setCreateError] = useState<string | null>(null);

  const tagItems = (tags.data as { data?: TagItem[] } | undefined)?.data ?? [];

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateError(null);
    const name = newName.trim();
    if (!name) return;
    try {
      await createTag.mutateAsync({ name, color: newColor });
      setNewName('');
      setNewColor(DEFAULT_TAG_COLOR);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Could not create tag.');
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="text-foreground text-[28px] font-semibold tracking-[-0.025em]">
            Workspace settings
          </h1>
          <p className="text-foreground-faint mt-1 text-[12.5px]">
            Manage statuses, tags, and members.
          </p>
        </div>

        <WorkspaceSection workspaceSlug={workspaceSlug} />

        <WorkspaceImageSection workspaceSlug={workspaceSlug} />

        <GithubSection workspaceSlug={workspaceSlug} />

        <StatusesSection workspaceSlug={workspaceSlug} />

        <section className="space-y-4">
          <div>
            <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Tags</h2>
            <p className="text-foreground-faint text-[12.5px]">
              Tags are workspace-scoped and can be applied to any task.
            </p>
          </div>

          <form
            onSubmit={handleCreate}
            className="bg-[var(--surface-quiet)] flex flex-wrap items-center gap-2 rounded-lg border p-3"
          >
            <Input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Tag name"
              maxLength={60}
              className="min-w-40 flex-1"
            />
            <ColorSwatchPicker value={newColor} onChange={setNewColor} />
            <Button type="submit" disabled={createTag.isPending || !newName.trim()}>
              Add tag
            </Button>
          </form>
          {createError ? (
            <p className="text-destructive text-sm" role="alert">
              {createError}
            </p>
          ) : null}

          {tags.isLoading ? (
            <SkeletonList rows={3} />
          ) : tagItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-8 text-center backdrop-blur-md">
              <p className="text-foreground-faint text-[12.5px]">No tags yet. Create one above.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--glass-card-border)] rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] backdrop-blur-md">
              {tagItems.map((tag) => (
                <TagRow key={tag.id} workspaceSlug={workspaceSlug} tag={tag} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  Workspace — name + task prefix (slice 10)
// ──────────────────────────────────────────────────────────────────────

interface WorkspaceSectionProps {
  workspaceSlug: string;
}

interface WorkspaceAttrs {
  name?: string;
  task_prefix?: string;
}

/**
 * Editable workspace-level fields. Slice 10 introduces `task_prefix`
 * — a 2-10 char code rendered as `{prefix}-{seq}` in front of every
 * task title. Renaming reflows every existing task code on next read,
 * so we surface a confirm dialog before saving.
 *
 * Admin-gated server-side; the form just renders disabled if the
 * mutation 403s. (We don't pre-filter via `useMyWorkspaceMembership`
 * here because the existing settings page doesn't either — server
 * authoritative is the convention.)
 */
const WorkspaceSection = ({ workspaceSlug }: WorkspaceSectionProps) => {
  const workspace = useWorkspace(workspaceSlug);
  const update = useUpdateWorkspace(workspaceSlug);
  const { confirm, dialog } = useConfirm();

  const attrs = (workspace.data as { data?: { attributes?: WorkspaceAttrs } } | undefined)?.data
    ?.attributes;

  const [name, setName] = useState('');
  const [taskPrefix, setTaskPrefix] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Hydrate from server on first load + on workspace switch.
  useMemo(() => {
    if (!attrs) return;
    setName(attrs.name ?? '');
    setTaskPrefix(attrs.task_prefix ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrs?.name, attrs?.task_prefix]);

  const dirty
    = (attrs?.name ?? '') !== name.trim()
    || (attrs?.task_prefix ?? '') !== taskPrefix.trim();

  const handleSave = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    const trimmedPrefix = taskPrefix.trim();

    /*
      Renaming the prefix re-renders every existing task code (FLUX-42
      becomes ABC-42 etc.). Sequence numbers are stable but the visible
      identifiers all shift, which is jarring without a heads-up.
    */
    const prefixChanged = trimmedPrefix !== (attrs?.task_prefix ?? '');
    if (prefixChanged) {
      const ok = await confirm({
        title: `Change task prefix to "${trimmedPrefix}"?`,
        description:
          'Every existing task will re-render with the new prefix on the next page load (e.g. "ABC-42" becomes "' + trimmedPrefix + '-42"). Sequence numbers stay the same.',
        confirmLabel: 'Change prefix',
      });
      if (!ok) return;
    }

    try {
      await update.mutateAsync({
        name: name.trim() === (attrs?.name ?? '') ? undefined : name.trim(),
        task_prefix: prefixChanged ? trimmedPrefix : undefined,
      });
      toast.success('Workspace updated');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save workspace.');
    }
  };

  return (
    <section className="space-y-4 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
      {dialog}
      <header>
        <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Workspace</h2>
        <p className="text-foreground-faint text-[12.5px]">
          Name and task identifier prefix. Admin or owner role required.
        </p>
      </header>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="workspace-name">Name</Label>
          <Input
            id="workspace-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="workspace-task-prefix">Task prefix</Label>
          <Input
            id="workspace-task-prefix"
            type="text"
            value={taskPrefix}
            onChange={(e) => setTaskPrefix(e.target.value.toUpperCase())}
            placeholder="FLUX"
            maxLength={10}
            pattern="[A-Z][A-Z0-9]{1,9}"
            className="font-mono"
            required
          />
          <p className="text-foreground-faint text-[11.5px]">
            2–10 uppercase letters and digits, starts with a letter. Renders as{' '}
            <span className="font-mono">{taskPrefix || 'PREFIX'}-42</span> on every task.
          </p>
        </div>

        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </form>
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  Workspace image (Slice 3 — workspace logo upload)
// ──────────────────────────────────────────────────────────────────────

interface WorkspaceImageSectionProps {
  workspaceSlug: string;
}

interface WorkspaceImageAttrs {
  image_url?: string | null;
}

/** Mirror of `App\Support\WorkspaceImageStorage::ACCEPTED_MIMES` —
 *  the file picker restricts client-side; server re-validates. */
const ACCEPTED_IMAGE_MIMES = 'image/png,image/jpeg,image/webp,image/svg+xml';
/** Server cap is 2 MB (2048 KB); we pre-check client-side so the
 *  user gets feedback in <1ms instead of after a multipart roundtrip. */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * Workspace logo upload + remove. Replaces the SPA's default
 * FluxterMark in the sidebar workspace switcher when set. Admin /
 * owner gated server-side (the form just surfaces 403 errors).
 *
 * Flow:
 *   1. Native `<input type="file">` (visually hidden, triggered by a
 *      labelled button) restricted to png/jpeg/webp/svg.
 *   2. Client validates size + mime; rejected uploads never hit the
 *      server.
 *   3. Multipart POST to /v1/workspaces/{slug}/image; mutation
 *      invalidates workspace caches so the new `image_url` shows up
 *      in the sidebar within the same render frame as the toast.
 *   4. "Remove" calls DELETE; idempotent server-side, so the button
 *      simply renders disabled when no image is set.
 */
const WorkspaceImageSection = ({ workspaceSlug }: WorkspaceImageSectionProps) => {
  const workspace = useWorkspace(workspaceSlug);
  const upload = useUploadWorkspaceImage(workspaceSlug);
  const remove = useDeleteWorkspaceImage(workspaceSlug);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const attrs = (workspace.data as { data?: { attributes?: WorkspaceImageAttrs } } | undefined)
    ?.data?.attributes;
  const imageUrl = attrs?.image_url ?? null;

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setError(null);
    const file = event.target.files?.[0];
    // Reset the input value so re-selecting the SAME file fires
    // `onChange` again (it wouldn't otherwise — same File reference).
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_IMAGE_BYTES) {
      setError('Image must be 2 MB or smaller.');
      return;
    }
    if (!ACCEPTED_IMAGE_MIMES.split(',').includes(file.type)) {
      setError('Use PNG, JPEG, WebP, or SVG.');
      return;
    }

    try {
      await upload.mutateAsync(file);
      toast.success('Workspace logo updated');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload image.');
    }
  };

  const handleRemove = async (): Promise<void> => {
    setError(null);
    try {
      await remove.mutateAsync();
      toast.success('Workspace logo removed');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove image.');
    }
  };

  const busy = upload.isPending || remove.isPending;

  return (
    <section className="space-y-4 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
      <header>
        <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Logo</h2>
        <p className="text-foreground-faint text-[12.5px]">
          A square PNG, JPEG, WebP, or SVG up to 2 MB. Replaces the default Fluxter mark in the
          sidebar.
        </p>
      </header>

      <div className="flex items-center gap-4">
        <div className="border-border bg-[var(--surface-quiet)] flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Workspace logo"
              className="size-full object-cover"
              // Never cache long — when the user replaces the logo the
              // path changes (random suffix) but the surrounding URL
              // origin is constant; the browser would otherwise serve
              // a stale image until the workspace query refetches.
            />
          ) : (
            <FluxterMark className="text-foreground-muted size-8" strokeWidth={2.5} />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_MIMES}
            onChange={handleFileChange}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus className="mr-1.5 size-3.5" aria-hidden="true" />
              {imageUrl ? 'Replace' : 'Upload'}
            </Button>
            {imageUrl ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => void handleRemove()}
                disabled={busy}
                className="text-foreground-muted hover:text-foreground"
              >
                <Trash2 className="mr-1.5 size-3.5" aria-hidden="true" />
                Remove
              </Button>
            ) : null}
          </div>
          {busy ? (
            <p className="text-foreground-faint text-[11.5px]">
              {upload.isPending ? 'Uploading…' : 'Removing…'}
            </p>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  GitHub App installation (slice 11)
// ──────────────────────────────────────────────────────────────────────

interface GithubSectionProps {
  workspaceSlug: string;
}

// Slice 11 + fix/github-setup-url-workspace-agnostic — handoff
// localStorage keys shared with `GithubInstallCallbackPage`. We stash
// both the signed state token AND the workspace slug the user was on
// when they clicked Install. The callback page (a top-level route, not
// nested under a workspace) uses the slug to navigate them back to
// the right `/w/{slug}/settings` after success.
const GITHUB_INSTALL_STATE_KEY = 'fluxter:github-install-state';
const GITHUB_INSTALL_WORKSPACE_KEY = 'fluxter:github-install-workspace';

/**
 * Org-owner-only "Connect to GitHub" card. Two render shapes:
 *
 * - **Not installed:** a primary "Install on GitHub" button + a
 *   one-paragraph explanation of what it unlocks.
 * - **Installed:** the GitHub account login + an "Install settings on
 *   GitHub" deep-link + a "Disconnect" button (with a confirm dialog
 *   since it strands every project's repo link).
 *
 * The install flow:
 *   1. User clicks "Install on GitHub" → we mint a signed `state`
 *      token via `useGetGithubInstallUrl`.
 *   2. We stash the state + the current workspace slug in
 *      `localStorage` (the callback page is workspace-agnostic at
 *      `/settings/github/callback` since a single GitHub App can
 *      configure only one Setup URL — see
 *      `fix/github-setup-url-workspace-agnostic`). The slug lets the
 *      callback page navigate the user back to the right
 *      `/w/{slug}/settings` after success.
 *   3. The GitHub App's configured Setup URL points at our SPA's
 *      top-level `/settings/github/callback` page, which reads
 *      `installation_id + state` from the URL and posts them to our
 *      install-callback endpoint.
 *
 * Org-owner gating: server-enforced. We additionally hide the card
 * when the current user isn't owner of THIS workspace — that's a
 * reasonable proxy for "org owner" since the founding user is owner
 * of the first workspace, and typical v1 orgs are single-workspace.
 * Non-owners see no card; admins see no card; only owners see it.
 */
const GithubSection = ({ workspaceSlug }: GithubSectionProps) => {
  const membership = useMyWorkspaceMembership(workspaceSlug);
  const installation = useGithubInstallation();
  const getInstallUrl = useGetGithubInstallUrl();
  const disconnect = useDeleteGithubInstallation();
  const { confirm, dialog } = useConfirm();

  // Hide entirely for non-owners. The query above wouldn't even fire
  // a request if we gated on membership before mounting the hook,
  // but the server's 403/404 are also caught (`retry: false`) so
  // there's no noisy fallback either way.
  if (! membership.isOwner) {
    return null;
  }

  const installed = installation.data as
    | { data?: { attributes?: { account_login?: string; github_installation_id?: number } } }
    | null
    | undefined;
  const accountLogin = installed?.data?.attributes?.account_login ?? null;
  const githubInstallationId = installed?.data?.attributes?.github_installation_id ?? null;

  const handleInstall = async (): Promise<void> => {
    try {
      const result = await getInstallUrl.mutateAsync();
      const url = result?.data?.url;
      const state = result?.data?.state;
      if (! url || ! state) {
        toast.error('Could not start the GitHub install flow.');
        return;
      }
      // Stash the state so the callback page can verify it round-tripped
      // unmodified before POSTing it to the install-callback endpoint,
      // plus the workspace slug so the callback knows where to bounce
      // the user back to. `localStorage` (not `sessionStorage`) for
      // reliable cross-tab access — see callback page comments.
      localStorage.setItem(GITHUB_INSTALL_STATE_KEY, state);
      localStorage.setItem(GITHUB_INSTALL_WORKSPACE_KEY, workspaceSlug);
      // Open in a new tab so the user can return without losing this page.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start the GitHub install flow.');
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Disconnect GitHub?',
      description:
        'Every project loses its repo link locally and "Create branch" stops working. The GitHub App stays installed on your account — visit GitHub to fully uninstall.',
      confirmLabel: 'Disconnect',
    });
    if (! ok) return;
    try {
      await disconnect.mutateAsync();
      toast.success('GitHub disconnected');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not disconnect.');
    }
  };

  return (
    <section className="space-y-4 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
      {dialog}
      <header>
        <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">GitHub</h2>
        <p className="text-foreground-faint text-[12.5px]">
          Link engineering projects to GitHub repos and create branches from tasks. Owner only.
        </p>
      </header>

      {installation.isLoading ? (
        <p className="text-foreground-faint text-[12.5px]">Loading…</p>
      ) : accountLogin ? (
        <div className="space-y-3">
          <div className="bg-[var(--surface-quiet)] flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm">
            <div className="min-w-0">
              <p className="text-foreground font-medium">Connected to {accountLogin}</p>
              <p className="text-foreground-faint text-[11.5px]">
                Engineering projects can now link a repo on the project settings page.
              </p>
            </div>
            {githubInstallationId !== null ? (
              <a
                href={`https://github.com/settings/installations/${githubInstallationId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 shrink-0 text-xs underline-offset-2 hover:underline"
              >
                Manage on GitHub
              </a>
            ) : null}
          </div>
          <BotCommentToggle workspaceSlug={workspaceSlug} />

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleDisconnect()}
            disabled={disconnect.isPending}
          >
            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          onClick={() => void handleInstall()}
          disabled={getInstallUrl.isPending}
        >
          {getInstallUrl.isPending ? 'Preparing…' : 'Install on GitHub'}
        </Button>
      )}
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  Bot comment toggle (slice 12)
// ──────────────────────────────────────────────────────────────────────

interface BotCommentToggleProps {
  workspaceSlug: string;
}

interface WorkspaceBotAttrs {
  github_bot_comment_enabled?: boolean;
}

/**
 * Per-workspace toggle for the PR-body bot comment that mirrors
 * linked Fluxter tasks. Default `true`. Disabling silences only the
 * public PR comment; the in-app linking surface (the Pull Requests
 * card on TaskDetailPage) keeps working.
 *
 * Inline render under the GitHub install card: the only meaningful
 * configuration available once an installation is connected.
 */
const BotCommentToggle = ({ workspaceSlug }: BotCommentToggleProps) => {
  const workspace = useWorkspace(workspaceSlug);
  const update = useUpdateWorkspace(workspaceSlug);

  const attrs = (workspace.data as { data?: { attributes?: WorkspaceBotAttrs } } | undefined)
    ?.data?.attributes;
  const enabled = attrs?.github_bot_comment_enabled ?? true;

  const handleToggle = async (next: boolean): Promise<void> => {
    try {
      await update.mutateAsync({ github_bot_comment_enabled: next });
      toast.success(next ? 'Bot comment enabled' : 'Bot comment silenced');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save toggle.');
    }
  };

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] px-4 py-3 text-[13px] backdrop-blur-md">
      <div className="min-w-0 space-y-1">
        <p className="text-foreground font-medium">Comment on PRs</p>
        <p className="text-foreground-faint text-[11.5px]">
          Posts a "Linked Fluxter tasks" comment on every PR that references a task code.
          The comment is auto-edited as references change. Turn off to silence only the
          public PR comment — the in-app PR list keeps working either way.
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={update.isPending}
        onClick={() => void handleToggle(! enabled)}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
          enabled ? 'bg-primary' : 'bg-muted-foreground/30',
          update.isPending && 'opacity-60',
        )}
      >
        <span
          className={cn(
            'inline-block size-4 transform rounded-full bg-white transition-transform',
            enabled ? 'translate-x-4.5' : 'translate-x-0.5',
          )}
        />
      </button>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  Statuses
// ──────────────────────────────────────────────────────────────────────

interface StatusesSectionProps {
  workspaceSlug: string;
}

const StatusesSection = ({ workspaceSlug }: StatusesSectionProps) => {
  const statuses = useWorkspaceStatuses(workspaceSlug);
  const reorder = useReorderTaskStatus(workspaceSlug);

  const items = (statuses.data as { data?: StatusItem[] } | undefined)?.data ?? [];

  const grouped = useMemo(() => {
    const out: Record<StatusCategory, StatusItem[]> = {
      todo: [],
      in_progress: [],
      complete: [],
    };
    for (const item of items) {
      const cat = item.attributes?.category;
      if (cat) out[cat].push(item);
    }
    for (const cat of CATEGORY_ORDER) {
      out[cat].sort(
        (a, b) => (a.attributes?.position ?? 0) - (b.attributes?.position ?? 0),
      );
    }
    return out;
  }, [items]);

  // Single DndContext spans all three categories so the user can grab any
  // status. Drops are constrained by checking the destination category in
  // `handleDragEnd` — cross-category drags are silently no-op'd because
  // the API treats categories as immutable.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    const activeStatus = items.find((s) => s.id === activeId);
    const overStatus = items.find((s) => s.id === overId);
    if (!activeStatus || !overStatus) return;

    if (activeStatus.attributes?.category !== overStatus.attributes?.category) {
      // Categories are immutable — silently ignore cross-category drops.
      return;
    }

    const targetPosition = overStatus.attributes?.position ?? 1;
    reorder.mutate(
      { id: activeId, position: targetPosition },
      {
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Could not reorder status. Reverted.');
        },
      },
    );
  };

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Statuses</h2>
        <p className="text-foreground-faint text-[12.5px]">
          Customize the named statuses inside each category. Categories themselves are fixed.
          One status per category is always the default — promoting a different status auto-flips
          the previous one off.
        </p>
      </div>

      {statuses.isLoading ? (
        <SkeletonStack lines={6} />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="space-y-6">
            {CATEGORY_ORDER.map((category) => (
              <CategoryGroup
                key={category}
                workspaceSlug={workspaceSlug}
                category={category}
                statuses={grouped[category]}
              />
            ))}
          </div>
        </DndContext>
      )}
    </section>
  );
};

interface CategoryGroupProps {
  workspaceSlug: string;
  category: StatusCategory;
  statuses: StatusItem[];
}

const CategoryGroup = ({ workspaceSlug, category, statuses }: CategoryGroupProps) => {
  const create = useCreateTaskStatus(workspaceSlug);
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftColor, setDraftColor] = useState<string>(DEFAULT_STATUS_COLOR);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const name = draftName.trim();
    if (!name) return;
    try {
      await create.mutateAsync({ name, category, color: draftColor });
      setDraftName('');
      setDraftColor(DEFAULT_STATUS_COLOR);
      setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create status.');
    }
  };

  return (
    <div className="space-y-2">
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-foreground-faint text-[11.5px] font-semibold tracking-[0.06em] uppercase">
          {CATEGORY_LABEL[category]}
        </h3>
        <span className="text-foreground-faint text-[11.5px] tabular-nums">
          {statuses.length}
        </span>
      </header>

      <ul className="divide-y divide-[var(--glass-card-border)] rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] backdrop-blur-md">
        <SortableContext
          items={statuses.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {statuses.length === 0 ? (
            <li className="text-muted-foreground px-4 py-3 text-sm italic">
              No statuses in this category.
            </li>
          ) : (
            statuses.map((status) => (
              <StatusRow key={status.id} workspaceSlug={workspaceSlug} status={status} />
            ))
          )}
        </SortableContext>
      </ul>

      {adding ? (
        <form
          onSubmit={handleSubmit}
          className="bg-[var(--surface-quiet)] flex flex-wrap items-center gap-2 rounded-md border p-2"
        >
          <Input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder={`New ${CATEGORY_LABEL[category].toLowerCase()} status`}
            maxLength={60}
            autoFocus
            className="min-w-40 flex-1"
          />
          <ColorSwatchPicker value={draftColor} onChange={setDraftColor} />
          <Button type="submit" size="sm" disabled={create.isPending || !draftName.trim()}>
            Add
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setAdding(false);
              setDraftName('');
              setDraftColor(DEFAULT_STATUS_COLOR);
              setError(null);
            }}
          >
            Cancel
          </Button>
          {error ? (
            <p className="text-destructive w-full text-xs" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdding(true)}
          className="text-muted-foreground hover:text-foreground -ml-2"
        >
          + Add status
        </Button>
      )}
    </div>
  );
};

interface StatusRowProps {
  workspaceSlug: string;
  status: StatusItem;
}

const StatusRow = ({ workspaceSlug, status }: StatusRowProps) => {
  const update = useUpdateTaskStatus(workspaceSlug);
  const archive = useArchiveTaskStatus(workspaceSlug);
  const { confirm, dialog } = useConfirm();
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(status.attributes?.name ?? '');
  const [color, setColor] = useState<string>(status.attributes?.color ?? DEFAULT_STATUS_COLOR);
  const [error, setError] = useState<string | null>(null);

  const isDefault = Boolean(status.attributes?.is_default);

  const handleSave = async () => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await update.mutateAsync({
        id: status.id,
        name: trimmed,
        color,
      });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save status.');
    }
  };

  const handlePromoteDefault = async () => {
    if (isDefault) return;
    setError(null);
    try {
      await update.mutateAsync({ id: status.id, is_default: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not set as default.');
    }
  };

  const handleArchive = async () => {
    const ok = await confirm({
      title: `Archive "${status.attributes?.name ?? 'this status'}"?`,
      description:
        'Tasks currently using this status will be moved to the category default.',
      confirmLabel: 'Archive',
      destructive: true,
    });
    if (!ok) return;
    try {
      await archive.mutateAsync(status.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not archive status.');
    }
  };

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (editing) {
    return (
      <li
        ref={setNodeRef}
        style={sortableStyle}
        className={cn('space-y-2 px-3 py-2', isDragging && 'opacity-50')}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            autoFocus
            className="min-w-40 flex-1"
          />
          <ColorSwatchPicker value={color} onChange={setColor} />
          <Button type="button" size="sm" onClick={handleSave} disabled={update.isPending || !name.trim()}>
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditing(false);
              setName(status.attributes?.name ?? '');
              setColor(status.attributes?.color ?? DEFAULT_STATUS_COLOR);
              setError(null);
            }}
          >
            Cancel
          </Button>
        </div>
        {error ? (
          <p className="text-destructive text-xs" role="alert">
            {error}
          </p>
        ) : null}
        {dialog}
      </li>
    );
  }

  return (
    <li
      ref={setNodeRef}
      style={sortableStyle}
      className={cn(
        'group/row flex items-center gap-2 px-3 py-2 transition-colors',
        isDragging ? 'opacity-50' : 'hover:bg-[var(--surface-quiet)]',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="text-muted-foreground/40 hover:text-muted-foreground -ml-1 cursor-grab touch-none p-0.5 transition-colors active:cursor-grabbing"
        aria-label={`Drag ${status.attributes?.name ?? 'status'}`}
      >
        <GripVertical className="size-4" />
      </button>
      <span
        className="size-3 shrink-0 rounded-full"
        style={{ background: status.attributes?.color ?? DEFAULT_STATUS_COLOR }}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="hover:text-foreground min-w-0 flex-1 text-left text-sm transition-colors"
      >
        <span className="truncate">{status.attributes?.name ?? 'Untitled'}</span>
        {isDefault ? (
          <span className="text-muted-foreground ml-2 text-xs">Default</span>
        ) : null}
      </button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void handlePromoteDefault()}
        disabled={isDefault || update.isPending}
        aria-label={isDefault ? 'This status is the category default' : 'Set as default'}
        className={cn(
          'size-7 p-0',
          isDefault
            ? 'text-[var(--brand-accent)]'
            : 'text-muted-foreground/40 hover:text-foreground opacity-0 group-hover/row:opacity-100',
        )}
      >
        <Star className={cn('size-3.5', isDefault && 'fill-current')} aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void handleArchive()}
        disabled={archive.isPending}
        aria-label={`Archive ${status.attributes?.name ?? 'status'}`}
        className="text-muted-foreground/40 hover:text-destructive size-7 p-0 opacity-0 transition-opacity group-hover/row:opacity-100"
      >
        <Trash2 className="size-3.5" aria-hidden="true" />
      </Button>
      {dialog}
    </li>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  Tags (existing)
// ──────────────────────────────────────────────────────────────────────

interface TagRowProps {
  workspaceSlug: string;
  tag: TagItem;
}

const TagRow = ({ workspaceSlug, tag }: TagRowProps) => {
  const update = useUpdateTag(workspaceSlug, tag.id);
  const remove = useDeleteTag(workspaceSlug);
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.attributes?.name ?? '');
  const [color, setColor] = useState<string>(tag.attributes?.color ?? DEFAULT_TAG_COLOR);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    try {
      await update.mutateAsync({ name: name.trim(), color });
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save tag.');
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: `Delete the tag "${tag.attributes?.name}"?`,
      description: 'It will be removed from every task that uses it.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setError(null);
    try {
      await remove.mutateAsync(tag.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete tag.');
    }
  };

  if (editing) {
    return (
      <li className="space-y-2 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            autoFocus
            className="min-w-40 flex-1"
          />
          <ColorSwatchPicker value={color} onChange={setColor} />
          <Button type="button" onClick={handleSave} disabled={update.isPending || !name.trim()}>
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setEditing(false);
              setName(tag.attributes?.name ?? '');
              setColor(tag.attributes?.color ?? DEFAULT_TAG_COLOR);
              setError(null);
            }}
          >
            Cancel
          </Button>
        </div>
        {error ? (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        ) : null}
        {dialog}
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between px-4 py-2.5">
      <TagChip name={tag.attributes?.name ?? 'tag'} color={tag.attributes?.color ?? '#64748b'} />
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={remove.isPending}
          className="text-destructive hover:text-destructive"
        >
          Delete
        </Button>
      </div>
      {dialog}
    </li>
  );
};

